import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toggleSidebar, useDataProvider, useListContext, useNotify, useRecordContext, useTranslate } from "react-admin"
import { Grid, IconButton, makeStyles, Paper, Tooltip } from "@material-ui/core"
import { EventBusy } from "@material-ui/icons"

import { SeasonClickProvider, useSeasonClickContext } from "../../contexts/SeasonClickContext"
import { SchedulingProvider, useSchedulingContext } from "../../contexts/SchedulingContext"
import { CalendarProvider } from "../../contexts/CalendarContext"
import { DragProvider } from "../../contexts/DragContext"

import { isAuthorized } from "../../../Authorize"
import ListCard from "../../../cards/ListCard"
import { RefreshCalendarButton } from "../league/SchedulerView"
import EventCalendar from "../../../../resources/events/EventCalendar"
import { ShareDraftGameAction } from "../../../../resources/draftgames/DraftGameShareModal"
import { PublishDraftGamesAction } from "../../../../resources/draftgames/DraftGamesPublishModal"
import { CALENDAR_VIEWS, EventViewSettingsMenu, useCalendarView, useCompact, useShowAwaySlots, useShowCondensedView, useShowOfficeName } from "../../../../resources/events/EventViewSettings"
import SeasonClickSidePanel from "./SeasonClickSidePanel"
import { isDraftGame } from "../../EventDetails"

const useStyles = makeStyles(theme => ({
  paper: {
    margin: theme.spacing(1),
    marginRight: 0,
    padding: theme.spacing(1),
  }
}))

const UnscheduleButton = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const { setRefreshAvailability } = useSchedulingContext();
  const { handleSelectCell } = useSeasonClickContext();
  const { refetch } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  if (!isDraftGame(record)) return null;

  const onClick = (e) => {
    e.stopPropagation();

    dataProvider.update('draftGames', {
      id: record.id,
      data: {
        date: null,
        startTime: null,
        endTime: null,
        arenaId: null,
        timezone: null,
      }
    })
      .then(() => {
        notify('components.calendar.messages.unschedule_success', 'success');
        refetch();
        setRefreshAvailability(true);
        handleSelectCell(null, null);
      })
      .catch(() => {
        notify('components.calendar.messages.unschedule_error', 'error');
      })
  }

  return <Tooltip title={translate('components.calendar.tooltips.unschedule')}>
    <IconButton onClick={onClick} size="small">
      <EventBusy fontSize="medium" />
    </IconButton>
  </Tooltip>
}

const Calendar = () => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, []);

  const popoverActions = [
    <UnscheduleButton key="unschedule" />,
  ];

  return (
    <DragProvider>
      <div ref={containerRef}>
        <CalendarProvider popoverActions={popoverActions} width={width}>
          <EventCalendar />
        </CalendarProvider>
      </div>
    </DragProvider>
  );
};

const View = () => {
  const classes = useStyles();

  return <SeasonClickProvider>
    <Grid container fullWidth spacing={0} style={{ flexWrap: 'nowrap' }}>
      <Grid item xs={10}>
        <Paper className={classes.paper}>
          <Calendar />
        </Paper>
      </Grid>
      <Grid item xs={2}>
        <SeasonClickSidePanel />
      </Grid>
    </Grid>
  </SeasonClickProvider>
}

export default () => {
  const schedule = useRecordContext()
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(state => state.admin.ui.sidebarOpen);

  const [ calendarView ] = useCalendarView();
  const [showAwaySlots, setShowAwaySlots] = useShowAwaySlots();
  const [showOfficeName, setShowOfficeName] = useShowOfficeName();
  const [condensedView, setCondensedView] = useShowCondensedView();
  const [compact, setCompact] = useCompact();

  const canShareDrafts = isAuthorized(schedule, 'schedules', 'share');
  const canPublishDrafts = isAuthorized(schedule, 'schedules', 'publish');

  useEffect(() => {
    if (isSidebarOpen) {
      dispatch(toggleSidebar())
    }

    setShowAwaySlots(false)
    setShowOfficeName(false)
    setCondensedView(false)
    setCompact(true)

    return () => setCompact(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions = [
    canShareDrafts && <ShareDraftGameAction key="share" schedule={schedule} />,
    canPublishDrafts && <PublishDraftGamesAction key="publish" schedule={schedule} />,
    <EventViewSettingsMenu
      key="settings"
      disableCalendar
      disableAssignments
      disableGroupRounds
      disableGroupArenas
      disableSeasonView={false}
      alwaysOn
      showOfficeNames
      showCondensedView
      showViewToggle
      showCompact
      showSurfaceSizes={false}
      showFlagsInput={false}
      showRenumber={false}
      showGameInfoInput={false}
    />,
    <RefreshCalendarButton key="refresh" />
  ]

  if (calendarView !== CALENDAR_VIEWS.SEASON_CLICK) return null;
  return <SchedulingProvider scheduleId={schedule?.id} officeId={schedule?.officeId}>
    <ListCard
      basePath="/surfaceEvents"
      resource="surfaceEvents"
      actions={actions}
      sort={{ field: 'startTime', order: 'ASC' }}
      filter={{ _scope: 'Tenant', scheduleId: schedule?.id, type: 'Draft Game' }}
      perPage={9999}
      noResultsText={false}
      componentProps={{ style: { overflow: 'visible' } }}
    >
      <View />
    </ListCard>
  </SchedulingProvider>
}
